import { useEffect } from 'react';
import { isTextEntryTarget } from '@utils/keyboard';

interface UseSingleKeyShortcutOptions {
  enabled?: boolean;
}

/**
 * Bind one bare single-key shortcut (no modifiers), matched
 * case-insensitively — the generalized form of the report page's G/E
 * bindings. Guards: never fires while the user types, while a modifier is
 * held, or when the event was already handled (e.g. an open menu).
 *
 * Pass a stable handler (useCallback) — the listener re-binds when it changes.
 */
export const useSingleKeyShortcut = (
  key: string,
  handler: () => void,
  { enabled = true }: UseSingleKeyShortcutOptions = {},
): void => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;
      if (event.key.toLowerCase() !== key.toLowerCase()) return;

      event.preventDefault();
      handler();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, handler, enabled]);
};
